import React, { useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Anchor, CalendarRange, Cog, Grid3X3, LayoutDashboard, ListChecks, Settings2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useResource } from "@/hooks/use-resource";
import { Panel, PanelRow, SectionLabel, Count } from "@/components/layout/Panel";
import { GROUPS, isoWeekOf, listParam, toggleIn, viewOf, weekNo, withPlanParams } from "@/lib/kapasitet";

export { withPlanParams };

/** A sub-choice under the active view: a small checkbox, the label, a quiet count. */
function CheckRow({ to, on, label, right, dot, testId }) {
  return (
    <PanelRow to={to} active={false} testId={testId} className="h-8 pl-[30px] text-[13px] font-normal text-foreground/80">
      <span className={cn("flex h-4 w-4 shrink-0 items-center justify-center rounded-[5px] border transition-colors", on ? "border-[hsl(var(--navy))] bg-[hsl(var(--navy))] text-white" : "border-black/[0.2] bg-white")} aria-hidden>{on ? <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2.5 6.5l2.5 2.5 4.5-5" /></svg> : null}</span>
      {dot ? <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: dot }} aria-hidden /> : null}
      <span className={cn("min-w-0 flex-1 truncate", !on && "text-muted-foreground")}>{label}</span>
      {right ? <span className="ml-auto shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">{right}</span> : null}
    </PanelRow>
  );
}

/**
 * Plan – the panel *is* the navigation. The five surfaces (the calendar with its two sheets as children), then the
 * choices that belong to the active surface: which resource groups the calendar shows, which trades the grid shows,
 * which kinds of commitments the register lists. «Hva om vi vinner …» below, the rules at the bottom.
 */
export function PlanPanel({ onCollapse, onSearch }) {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const onPlan = pathname === "/plan";
  const visning = viewOf(params.get("visning"));
  const uker = Number(params.get("uker") || 8);
  const fra = params.get("fra") || isoWeekOf();
  const hvaom = listParam(params, "hvaom");
  const skjul = listParam(params, "skjul");
  const skjulFag = listParam(params, "skjulfag");
  const type = params.get("type") || "alle";
  const { data } = useResource("/kapasitet/puls", { fra, uker }, { interval: 90000 });
  const settings = useResource("/kapasitet/innstillinger", null, { interval: 300000 });
  const belegg = useResource("/kapasitet/belegg", null, { interval: 90000, enabled: onPlan && visning === "belegg" });
  const saker = useMemo(() => (data ? [...data.tilbud.saker].sort((a, b) => b.sannsynlighet - a.sannsynlighet) : []), [data]);
  const link = (patch) => withPlanParams(params, patch);
  const view = (k) => link({ visning: k === "oversikt" ? null : k });
  const counts = data ? { oversikt: data.beslutninger.length, fag: data.fag.stramme.length, anlegg: data.konflikter.length, belegg: data.utloper.length } : {};
  const tone = { fag: data?.fag.stramme.some((c) => c.tilstand === "over") ? "brand" : "muted", anlegg: data?.konflikter.length ? "brand" : "muted" };
  const row = (k, Icon, label, cls) => <PanelRow key={k} to={view(k)} active={onPlan && visning === k} icon={Icon} label={label} right={counts[k] ? <Count n={counts[k]} tone={tone[k] || "muted"} testId={`panel-plan-count-${k}`} /> : null} testId={`panel-plan-${k}`} className={cls} />;
  const fag = (settings.data?.avdelinger || []).filter((a) => a.plan !== false);
  const typeCount = (k) => (belegg.data ? belegg.data.rader.filter((b) => k === "alle" || b.type === k).length : null);
  return (
    <Panel title="Plan" meta={`uke ${weekNo(fra)}–${weekNo(fra) + uker - 1}`} onCollapse={onCollapse} onSearch={onSearch} testId="panel-plan">
      <div className="scroll-fade-b min-h-0 flex-1 overflow-y-auto px-3 pb-4 scrollbar-thin">
        <SectionLabel>Visning</SectionLabel>
        <div className="space-y-px" data-testid="panel-plan-views">
          {row("oversikt", LayoutDashboard, "Oversikt")}
          <PanelRow to={view("anlegg")} active={false} icon={CalendarRange} label="Kalender" testId="panel-plan-kalender" className={cn(onPlan && (visning === "anlegg" || visning === "maskiner") && "text-foreground")} />
          <div className="space-y-px" data-testid="panel-plan-kalender-sub">
            {row("anlegg", Anchor, "Dokk, slipp og kai", "pl-[30px]")}
            {row("maskiner", Cog, "Maskiner", "pl-[30px]")}
          </div>
          {row("fag", Grid3X3, "Fag · uker")}
          {row("belegg", ListChecks, "Belegg")}
        </div>

        {onPlan && visning === "anlegg" ? (
          <>
            <SectionLabel className="mt-5" right={skjul.length ? <Link to={link({ skjul: null })} className="text-[11.5px] font-medium text-[hsl(var(--navy))] hover:underline" data-testid="panel-plan-skjul-clear">Vis alle</Link> : null}>Vis i kalenderen</SectionLabel>
            <div className="space-y-px" data-testid="panel-plan-grupper">
              {GROUPS.filter((g) => g.key !== "maskiner").map((g) => <CheckRow key={g.key} to={link({ skjul: toggleIn(skjul, g.key).join(",") || null })} on={!skjul.includes(g.key)} label={g.label} testId={`panel-plan-gruppe-${g.key}`} />)}
            </div>
          </>
        ) : null}

        {onPlan && visning === "fag" && fag.length ? (
          <>
            <SectionLabel className="mt-5" right={skjulFag.length ? <Link to={link({ skjulfag: null })} className="text-[11.5px] font-medium text-[hsl(var(--navy))] hover:underline" data-testid="panel-plan-skjulfag-clear">Vis alle</Link> : null}>Fag</SectionLabel>
            <div className="space-y-px" data-testid="panel-plan-fag">
              {fag.map((a) => { const n = data?.fag.stramme.filter((c) => c.avdeling === a.code).length; return <CheckRow key={a.code} to={link({ skjulfag: toggleIn(skjulFag, a.code).join(",") || null })} on={!skjulFag.includes(a.code)} label={a.name} dot={a.color} right={n ? `${n} stram${n > 1 ? "me" : ""}` : null} testId={`panel-plan-fag-${a.code}`} />; })}
            </div>
          </>
        ) : null}

        {onPlan && visning === "belegg" ? (
          <>
            <SectionLabel className="mt-5">Type</SectionLabel>
            <div className="space-y-px" data-testid="panel-plan-type">
              {[["alle", "Alle"], ["bekreftet", "Bekreftet"], ["reservasjon", "Reservasjoner"], ["tilbud", "Åpne tilbud"]].map(([k, l]) => (
                <PanelRow key={k} to={link({ type: k === "alle" ? null : k })} active={type === k} label={l} right={typeCount(k) != null ? <span className="font-mono text-[11px] tabular-nums text-muted-foreground">{typeCount(k)}</span> : null} testId={`panel-plan-type-${k}`} className="h-8 pl-[30px] text-[13px]" />
              ))}
            </div>
          </>
        ) : null}

        <SectionLabel className="mt-5" right={hvaom.length ? <Link to={link({ hvaom: null })} className="text-[11.5px] font-medium text-[hsl(var(--navy))] hover:underline" data-testid="panel-plan-hvaom-clear">Nullstill</Link> : null}>Hva om vi vinner …</SectionLabel>
        <div className="space-y-px" data-testid="panel-plan-hvaom">
          {!data ? <div className="px-2.5 py-2 text-[12px] text-muted-foreground">Laster …</div> : saker.length ? saker.slice(0, 12).map((p) => (
            <CheckRow key={p.sak.id} to={link({ hvaom: toggleIn(hvaom, p.sak.id).join(",") || null })} on={hvaom.includes(p.sak.id)} label={p.sak.name} right={p.reservert ? "res." : `${Math.round(p.sannsynlighet)} %`} testId={`panel-plan-hvaom-${p.sak.id}`} />
          )) : <div className="px-2.5 py-2 text-[12px] text-muted-foreground">Ingen åpne tilbud i perioden.</div>}
        </div>
        {hvaom.length ? <p className="px-2.5 pt-1.5 text-[11.5px] leading-snug text-muted-foreground">Legges oppå bekreftet og reservert – uvektet. Vises med ring i rutenettet.</p> : null}

        <SectionLabel className="mt-6">Oppsett</SectionLabel>
        <div className="space-y-px">
          <PanelRow to="/settings/plan" icon={Settings2} label="Kapasitet og regler" testId="panel-plan-settings" />
          {data?.demo ? <PanelRow to="/settings/plan?tab=demo" label="Demo-data i planen" right={<span className="inline-flex h-[18px] items-center rounded-full bg-[hsl(var(--v-assumed-bg))] px-1.5 font-mono text-[10px] font-semibold uppercase text-[hsl(var(--v-assumed))]">Demo</span>} testId="panel-plan-demo" className="pl-[30px] text-[13px]" /> : null}
        </div>
        <p className="mt-4 px-2.5 text-[11.5px] leading-snug text-muted-foreground">Løftekapasitet i tilbudsfasen – ikke produksjonsplan. Uker for fag, datoer for dokk, slipp og kai.</p>
      </div>
    </Panel>
  );
}
